import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * Surface — the single content container primitive. Replaces ad-hoc
 * `<div className="rounded-lg border bg-card …">` and the heavier
 * `<GlowCard>` for the common case.
 *
 *   <Surface>…</Surface>
 *   <Surface tone="muted">…</Surface>
 *   <Surface tone="raised" interactive>…</Surface>
 *
 * Tones:
 *   default  – card on canvas
 *   muted    – subtle inset (toolbars, panels-within-panels)
 *   raised   – stronger elevation (highlighted KPIs, hero cards)
 *   outline  – transparent w/ border (empty states, hint banners)
 */
const surfaceVariants = cva('rounded-xl border transition-colors', {
  variants: {
    tone: {
      default: 'border-border bg-card text-card-foreground shadow-elevation-sm',
      muted: 'border-border/60 bg-surface-2 text-foreground',
      raised: 'border-border bg-card text-card-foreground shadow-elevation-md',
      outline: 'border-border/70 bg-transparent text-foreground',
    },
    interactive: {
      true: 'hover:border-primary/40 hover:shadow-elevation-md',
      false: '',
    },
  },
  defaultVariants: { tone: 'default', interactive: false },
});

export interface SurfaceProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof surfaceVariants> {}

export const Surface = forwardRef<HTMLDivElement, SurfaceProps>(
  ({ className, tone, interactive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(surfaceVariants({ tone, interactive }), className)}
      {...props}
    />
  ),
);
Surface.displayName = 'Surface';

export { surfaceVariants };
